import {ChangeEvent, FormEvent, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import Pagination from 'rsuite/Pagination'
import Table from 'rsuite/Table'
import Toggle from 'rsuite/Toggle'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DeleteModal from 'src/app/modules/common/components/deleteModal'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import {ShareholderBatchModel, ShareholderListParams, ShareholderLookupType} from '../Model'
import {actions, IShareholderLookupState, service} from '../redux'

const Cell = Table.Cell

// Columns of the legacy Excel layout (A, B, ...), in order; row 1 of the file is a heading.
const COLUMNS: Record<ShareholderLookupType, [string, string][]> = {
  'bonus-tax': [
    ['boid', 'BOID'],
    ['customerName', 'Customer Name'],
    ['bonusTax', 'Bonus Tax'],
  ],
  'right-share': [
    ['shareHolderNo', 'Share Holder No'],
    ['boid', 'BOID'],
    ['customerName', 'Customer Name'],
    ['fatherName', 'Father Name'],
    ['grandFatherName', 'Grand Father Name'],
    ['physicalKitta', 'Physical Kitta'],
    ['phtfraKitta', 'Physical Fraction Kitta'],
    ['dematKitta', 'Demat Kitta'],
    ['dematFraction', 'Demat Fraction'],
    ['totalKitta', 'Total Kitta'],
    ['rightKitta', 'Right Kitta'],
    ['actualKitta', 'Actual Kitta'],
  ],
}

const errorText = (err: any) => {
  const errors = err?.response?.data?.errors
  if (errors) return Object.values(errors).flat().join('\n')
  return err?.response?.data?.message || 'Something went wrong'
}

/** Legacy /staff bonus_tax and right_share: Excel import per date, imported files, rows with search. */
const ShareholderLookup = ({type, moduleName}: {type: ShareholderLookupType; moduleName: string}) => {
  const dispatch = useDispatch()
  const state: IShareholderLookupState = useSelector((s: any) => s.shareholderLookup)
  const [params, setParams] = useState<ShareholderListParams>({type, page: 1, limit: 10})
  const [batches, setBatches] = useState<ShareholderBatchModel[]>([])
  const [file, setFile] = useState<File | null>(null)
  const [date, setDate] = useState('')
  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState<string[]>([])
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const loadBatches = () =>
    service
      .getBatches(type)
      .then((r) => setBatches(r?.data?.data?.batch || []))
      .catch((e) => toast.error(errorText(e)))
  const loadRecords = () => dispatch(actions.getShareholderRecords(params))

  useEffect(() => {
    loadBatches()
  }, [type])
  useEffect(() => {
    loadRecords()
  }, [params])

  const refresh = () => {
    loadBatches()
    loadRecords()
  }

  const handleImport = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!file) {
      toast.error('Choose an Excel file')
      return
    }
    const form = e.currentTarget
    setImporting(true)
    setImportErrors([])
    service
      .importFile(type, file, date || undefined)
      .then((r) => {
        toast.success(`Imported ${r?.data?.data?.rowCount} rows`)
        setFile(null)
        form.reset()
        refresh()
      })
      .catch((err) => {
        const errors = err?.response?.data?.errors
        if (errors) setImportErrors(Object.values(errors).flat() as string[])
        toast.error(errors ? 'Import rejected: fix the rows listed below' : errorText(err))
      })
      .finally(() => setImporting(false))
  }

  const downloadSample = () =>
    service
      .getSample(type)
      .then((r) => {
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(new Blob([r.data]))
        link.setAttribute('download', `${type}-sample.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      })
      .catch(() => toast.error('Sample file not available'))

  const toggleBatch = (b: ShareholderBatchModel) =>
    service
      .setBatchStatus(type, b.id, !b.isActive)
      .then(() => {
        toast.success(b.isActive ? 'Hidden from the website' : 'Shown on the website')
        loadBatches()
      })
      .catch((e) => toast.error(errorText(e)))

  const deleteBatch = () => {
    const id = deleteId
    setDeleteId(null)
    if (!id) return
    service
      .deleteBatch(type, id)
      .then(() => {
        toast.success('Deleted')
        if (params.batchId === id) setParams({...params, page: 1, batchId: undefined})
        refresh()
      })
      .catch((e) => toast.error(errorText(e)))
  }

  const columns = [
    {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
    {label: 'Import Date', dataKey: 'batchDate', width: 110, cell: <Cell dataKey='batchDate' />},
    ...COLUMNS[type].map(([key, label]) => ({
      label,
      dataKey: key,
      width: key.endsWith('Name') ? 180 : key === 'boid' ? 160 : 120,
      cell: <Cell dataKey={key} />,
    })),
  ]
  const data = (state?.data?.record || []).map((r, i) => ({
    ...r,
    sn: (params.page - 1) * params.limit + (i + 1),
  }))

  return (
    <div className='shadow p-3 bg-white rounded'>
      <DesignComponent
        moduleName={moduleName}
        addShow={false}
        deleteShow={false}
        toggleMultipleShow={false}
        importShow={false}
        exportShow={false}
        params={params}
        setParams={setParams}
        handleRefresh={refresh}
      />

      <CheckPermissions type='Import'>
        <form className='row align-items-end mt-3' onSubmit={handleImport} data-cy='shareholder-import-form'>
          <div className='col-md-4 mb-3'>
            <label className='form-label fw-bolder fs-7' htmlFor='shareholder-file'>
              Excel file (.xlsx, .xls or .csv)
            </label>
            <input
              id='shareholder-file'
              type='file'
              accept='.xlsx,.xls,.csv'
              className='form-control form-control-sm'
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className='col-md-3 mb-3'>
            <label className='form-label fw-bolder fs-7' htmlFor='shareholder-date'>
              Date (default today)
            </label>
            <input
              id='shareholder-date'
              type='date'
              className='form-control form-control-sm'
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className='col-md-5 mb-3'>
            <button type='submit' className='btn btn-primary btn-sm me-3' disabled={importing}>
              {importing ? 'Importing...' : 'Import'}
            </button>
            <button type='button' className='btn btn-link btn-sm' onClick={downloadSample}>
              Download sample file
            </button>
          </div>
          <div className='col-12 text-muted fs-7 mb-3'>
            Columns in order: {COLUMNS[type].map(([, label]) => label).join(', ')}. Row 1 is a heading. Format
            the BOID column as Text so Excel keeps all 16 digits. Importing again on the same date replaces that
            date&apos;s list; a file with any bad row is rejected as a whole.
          </div>
          {importErrors.length > 0 && (
            <div className='col-12'>
              <div className='alert alert-danger fs-7' role='alert' data-cy='shareholder-import-errors'>
                {importErrors.map((m) => (
                  <div key={m}>{m}</div>
                ))}
              </div>
            </div>
          )}
        </form>
      </CheckPermissions>

      <h6 className='mt-3'>Imported files</h6>
      <div className='table-responsive mb-5'>
        <table className='table table-sm table-row-bordered align-middle' data-cy='shareholder-batches'>
          <thead>
            <tr className='fw-bolder'>
              <th>Date</th>
              <th>File</th>
              <th>Rows</th>
              <th>Imported</th>
              <th>Shown on website</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} className={params.batchId === b.id ? 'table-active' : ''}>
                <td>{b.date}</td>
                <td>{b.fileName}</td>
                <td>{b.rowCount}</td>
                <td>{b.importedAt}</td>
                <td>
                  <CheckPermissions type='Edit'>
                    <Toggle size='sm' checked={b.isActive} onChange={() => toggleBatch(b)} />
                  </CheckPermissions>
                </td>
                <td className='text-end'>
                  <button
                    type='button'
                    className='btn btn-light btn-sm me-2'
                    onClick={() =>
                      setParams({...params, page: 1, batchId: params.batchId === b.id ? undefined : b.id})
                    }
                  >
                    {params.batchId === b.id ? 'All rows' : 'View rows'}
                  </button>
                  <CheckPermissions type='Delete'>
                    <button
                      type='button'
                      className='btn btn-light-danger btn-sm'
                      onClick={() => setDeleteId(b.id)}
                    >
                      Delete
                    </button>
                  </CheckPermissions>
                </td>
              </tr>
            ))}
            {batches.length === 0 && (
              <tr>
                <td colSpan={6} className='text-muted'>
                  No file imported yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='datatable'>
        <RSuiteTable columns={columns} data={data} showLoading={state?.loading} />
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          className='mt-5'
          maxButtons={5}
          size='sm'
          layout={['total', '-', 'limit', '|', 'pager', 'skip']}
          total={state?.data?.meta?.total || 0}
          limitOptions={[10, 20, 50, 100]}
          limit={params.limit}
          activePage={params.page}
          onChangePage={(page) => setParams({...params, page})}
          onChangeLimit={(limit) => setParams({...params, page: 1, limit})}
        />
      </div>

      <DeleteModal
        isOpen={!!deleteId}
        title='delete this file and all its rows'
        handleClick={deleteBatch}
        handleClose={() => setDeleteId(null)}
      />
    </div>
  )
}

export const BonusTax = () => <ShareholderLookup type='bonus-tax' moduleName='Bonus Tax' />
export const RightShare = () => <ShareholderLookup type='right-share' moduleName='Right Share' />

export default ShareholderLookup
